import { useEffect } from "react";

/**
 * Iqbal prototype (KNhbuC65doQnFKNhLTog6r / 120:1228): on restart the header
 * stays and the rest of the viewport dissolves in from opacity 0. MCP does not
 * export prototype interactions, so we play that start→visible dissolve here.
 *
 * `when: "mount"` — play after the first paint (hero load). Adding `is-in` in
 * the same turn as mount skips the transition: the first paint is already 1.
 * `when: "scroll"` — play the first time the node enters the viewport.
 */
function afterPaint(fn) {
  let inner = 0;
  const outer = requestAnimationFrame(() => {
    inner = requestAnimationFrame(fn);
  });
  return () => {
    cancelAnimationFrame(outer);
    cancelAnimationFrame(inner);
  };
}

export default function useOcReveal(containerRef, { when = "scroll" } = {}) {
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return undefined;

    const nodes = root.classList.contains("oc-reveal")
      ? [root, ...root.querySelectorAll(".oc-reveal")]
      : [...root.querySelectorAll(".oc-reveal")];
    if (!nodes.length) return undefined;

    const show = (list) => list.forEach((n) => n.classList.add("is-in"));

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      show(nodes);
      return undefined;
    }

    if (when === "mount") {
      return afterPaint(() => show(nodes));
    }

    const pending = new Map();
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          io.unobserve(entry.target);
          pending.set(
            entry.target,
            afterPaint(() => {
              entry.target.classList.add("is-in");
              pending.delete(entry.target);
            })
          );
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );
    nodes.forEach((n) => io.observe(n));
    return () => {
      io.disconnect();
      pending.forEach((cancel) => cancel());
    };
  }, [when]);
}
